import api from './api';

// Monitoring API service for dashboard metrics
export const monitoringApi = {
  // Get pending jobs count for each service from Pub/Sub queues
  getPendingJobsCount: async () => {
    try {
      const response = await api.get('/api/v1/monitoring/pending-jobs');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch pending jobs count:', error);
      // Return mock data for development/testing
      return {
        'frame-extractor': 0,
        'solo-analyzer': 0,
        'facemesh-analyzer': 0,
        'fast-aggregator': 0,
        'filtering-service': 0,
        'progress-service': 0,
        'finalizer-service': 0,
        'cost-service': 0,
        'web-api': 0,
        'web-client': 0
      };
    }
  },

  // Get active instances count for each Cloud Run service
  getActiveInstances: async () => {
    try {
      const response = await api.get('/api/v1/monitoring/active-instances');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch active instances:', error);
      // Return mock data for development/testing
      return {
        'frame-extractor': 2,
        'solo-analyzer': 3,
        'facemesh-analyzer': 1,
        'fast-aggregator': 2,
        'filtering-service': 1,
        'progress-service': 1,
        'finalizer-service': 1,
        'cost-service': 1,
        'web-api': 1,
        'web-client': 1
      };
    }
  },

  // Get resource utilization (CPU, memory, network) for each service
  getResourceUtilization: async () => {
    try {
      const response = await api.get('/api/v1/monitoring/resource-utilization');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch resource utilization:', error);
      // Return mock data for development/testing
      return {
        'frame-extractor': {
          cpu: 45.2,
          memory: 62.8,
          network: {
            bytesSent: 128,
            bytesReceived: 256
          }
        },
        'solo-analyzer': {
          cpu: 78.5,
          memory: 85.2,
          network: {
            bytesSent: 512,
            bytesReceived: 1024
          }
        },
        'facemesh-analyzer': {
          cpu: 23.1,
          memory: 34.7,
          network: {
            bytesSent: 64,
            bytesReceived: 128
          }
        },
        'fast-aggregator': {
          cpu: 56.8,
          memory: 71.3,
          network: {
            bytesSent: 256,
            bytesReceived: 512
          }
        },
        'filtering-service': {
          cpu: 15.2,
          memory: 22.1,
          network: {
            bytesSent: 32,
            bytesReceived: 64
          }
        },
        'progress-service': {
          cpu: 8.5,
          memory: 12.3,
          network: {
            bytesSent: 16,
            bytesReceived: 32
          }
        },
        'finalizer-service': {
          cpu: 6.1,
          memory: 9.8,
          network: {
            bytesSent: 8,
            bytesReceived: 16
          }
        },
        'cost-service': {
          cpu: 4.3,
          memory: 7.2,
          network: {
            bytesSent: 4,
            bytesReceived: 8
          }
        },
        'web-api': {
          cpu: 12.4,
          memory: 28.9,
          network: {
            bytesSent: 32,
            bytesReceived: 64
          }
        },
        'web-client': {
          cpu: 8.7,
          memory: 15.2,
          network: {
            bytesSent: 16,
            bytesReceived: 32
          }
        }
      };
    }
  },

  // Get frame processing time metrics for AI services with avg/min/max statistics
  getFrameProcessingTime: async () => {
    try {
      const response = await api.get('/api/v1/monitoring/frame-processing-time');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch frame processing time:', error);
      // Return mock data with avg/min/max structure for development/testing
      return {
        'solo-analyzer': { avg: 847, min: 593, max: 1271, samples: 0 },
        'facemesh-analyzer': { avg: 1234, min: 864, max: 1851, samples: 0 },
        'frame-extractor': { avg: 156, min: 109, max: 234, samples: 0 },
        'fast-aggregator': { avg: 89, min: 62, max: 134, samples: 0 },
        'filtering-service': { avg: 42, min: 29, max: 63, samples: 0 },
        'progress-service': { avg: 28, min: 20, max: 42, samples: 0 },
        'finalizer-service': { avg: 15, min: 11, max: 23, samples: 0 },
        'cost-service': { avg: 12, min: 8, max: 18, samples: 0 }
      };
    }
  },

  // Get service health status based on actual health endpoints
  getServiceHealth: async () => {
    try {
      const response = await api.get('/api/v1/monitoring/service-health');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch service health:', error);
      // Return fallback data for development/testing
      return {
        'frame-extractor': { status: 'unknown', statusCode: null, responseTime: null },
        'solo-analyzer': { status: 'unknown', statusCode: null, responseTime: null },
        'facemesh-analyzer': { status: 'unknown', statusCode: null, responseTime: null },
        'fast-aggregator': { status: 'unknown', statusCode: null, responseTime: null },
        'filtering-service': { status: 'unknown', statusCode: null, responseTime: null },
        'progress-service': { status: 'unknown', statusCode: null, responseTime: null },
        'finalizer-service': { status: 'unknown', statusCode: null, responseTime: null },
        'cost-service': { status: 'unknown', statusCode: null, responseTime: null },
        'web-api': { status: 'up', statusCode: 200, responseTime: null },
        'web-client': { status: 'unknown', statusCode: null, responseTime: null }
      };
    }
  },

  // Get processing rates (messages/minute) for each service
  getProcessingRates: async () => {
    try {
      const response = await api.get('/api/v1/monitoring/processing-rates');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch processing rates:', error);
      // Return fallback zeros on error
      return {
        'frame-extractor': 0,
        'solo-analyzer': 0,
        'facemesh-analyzer': 0,
        'fast-aggregator': 0,
        'filtering-service': 0,
        'progress-service': 0,
        'finalizer-service': 0,
        'cost-service': 0,
        'web-api': 0,
        'web-client': 0
      };
    }
  },

  // Get current service configuration (resources, scaling, etc.)
  getCurrentConfiguration: async () => {
    try {
      const response = await api.get('/api/v1/monitoring/configuration');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch service configuration:', error);
      // Return mock data for development/testing
      return {
        'frame-extractor': {
          cpu: '8 CPU',
          memory: '16Gi',
          minInstances: 0,
          maxInstances: 25,
          concurrency: 1
        },
        'solo-analyzer': {
          cpu: '4 CPU',
          memory: '4Gi',
          minInstances: 2,
          maxInstances: 20,
          concurrency: 25
        },
        'facemesh-analyzer': {
          cpu: '4 CPU',
          memory: '4Gi',
          minInstances: 1,
          maxInstances: 15,
          concurrency: 20
        },
        'fast-aggregator': {
          cpu: '4 CPU',
          memory: '4Gi',
          minInstances: 2,
          maxInstances: 15,
          concurrency: 25
        },
        'filtering-service': {
          cpu: '2 CPU',
          memory: '2Gi',
          minInstances: 1,
          maxInstances: 8,
          concurrency: 20
        },
        'progress-service': {
          cpu: '2 CPU',
          memory: '2Gi',
          minInstances: 1,
          maxInstances: 5,
          concurrency: 15
        },
        'finalizer-service': {
          cpu: '2 CPU',
          memory: '2Gi',
          minInstances: 1,
          maxInstances: 3,
          concurrency: 10
        },
        'cost-service': {
          cpu: '2 CPU',
          memory: '2Gi',
          minInstances: 1,
          maxInstances: 3,
          concurrency: 10
        },
        'web-api': {
          cpu: '2 CPU',
          memory: '2Gi',
          minInstances: 1,
          maxInstances: 10,
          concurrency: 50
        },
        'web-client': {
          cpu: '1 CPU',
          memory: '1Gi',
          minInstances: 1,
          maxInstances: 5,
          concurrency: 100
        }
      };
    }
  },

  // Get service health status
  getServiceHealth: async () => {
    try {
      const response = await api.get('/api/v1/monitoring/health');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch service health:', error);
      // Return mock data for development/testing
      return {
        'frame-extractor': 'healthy',
        'solo-analyzer': 'healthy',
        'facemesh-analyzer': 'degraded',
        'fast-aggregator': 'healthy',
        'filtering-service': 'healthy',
        'progress-service': 'healthy',
        'finalizer-service': 'healthy',
        'cost-service': 'healthy',
        'web-api': 'healthy',
        'web-client': 'healthy'
      };
    }
  },

  // Get business metrics summary
  getBusinessMetrics: async (hours = 24) => {
    try {
      const response = await api.get(`/api/v1/monitoring/business-metrics?hours=${hours}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch business metrics:', error);
      // Return mock data for development/testing
      return {
        videosProcessed: 156,
        framesAnalyzed: 89234,
        avgProcessingTime: 847,
        successRate: 98.7,
        costPerVideo: 0.23,
        throughputVideosPerHour: 6.5,
        throughputFramesPerMinute: 61.8
      };
    }
  },

  // Get real-time alerts
  getAlerts: async () => {
    try {
      const response = await api.get('/api/v1/monitoring/alerts');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      // Return mock data for development/testing
      return [
        {
          id: '1',
          service: 'facemesh-analyzer',
          severity: 'warning',
          message: 'High memory usage detected',
          timestamp: new Date().toISOString(),
          acknowledged: false
        }
      ];
    }
  },

  // Acknowledge an alert
  acknowledgeAlert: async (alertId) => {
    try {
      const response = await api.post(`/api/v1/monitoring/alerts/${alertId}/acknowledge`);
      return response.data;
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
      throw error;
    }
  },

  // Resolve an alert
  resolveAlert: async (alertId) => {
    try {
      const response = await api.post(`/api/v1/monitoring/alerts/${alertId}/resolve`);
      return response.data;
    } catch (error) {
      console.error('Failed to resolve alert:', error);
      throw error;
    }
  },

  // Get monitoring dashboard configuration
  getDashboardConfig: async () => {
    try {
      const response = await api.get('/api/v1/monitoring/dashboard-config');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch dashboard config:', error);
      // Return default configuration
      return {
        refreshInterval: 30000, // 30 seconds
        autoRefresh: true,
        chartTimeRanges: ['1h', '6h', '24h', '7d', '30d'],
        defaultTimeRange: '24h',
        alertThresholds: {
          cpu: 80,
          memory: 85,
          disk: 90,
          errorRate: 10
        }
      };
    }
  },

  // Update dashboard configuration
  updateDashboardConfig: async (config) => {
    try {
      const response = await api.put('/api/v1/monitoring/dashboard-config', config);
      return response.data;
    } catch (error) {
      console.error('Failed to update dashboard config:', error);
      throw error;
    }
  },

  // Get service-level cost analysis
  getServiceLevelCosts: async (hours = 24) => {
    try {
      const response = await api.get(`/api/v1/monitoring/cost-analysis?hours=${hours}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch service-level costs:', error);
      // Return mock data for development/testing
      return {
        totalCost: 0.0542,
        costPerVideo: 0.0034,
        throughputVideosPerHour: 16.2,
        serviceCosts: {
          'frame-extractor': {
            totalCost: 0.0289,
            frameCount: 15420,
            avgProcessingTime: 1240,
            breakdown: {
              cpuCost: 0.0201,
              memoryCost: 0.0065,
              baseCost: 0.0023
            }
          },
          'solo-analyzer': {
            totalCost: 0.0156,
            frameCount: 15420,
            avgProcessingTime: 680,
            breakdown: {
              cpuCost: 0.0089,
              memoryCost: 0.0045,
              baseCost: 0.0022
            }
          },
          'fast-aggregator': {
            totalCost: 0.0067,
            frameCount: 15420,
            avgProcessingTime: 340,
            breakdown: {
              cpuCost: 0.0041,
              memoryCost: 0.0018,
              baseCost: 0.0008
            }
          },
          'facemesh-analyzer': {
            totalCost: 0.0030,
            frameCount: 8941,
            avgProcessingTime: 280,
            breakdown: {
              cpuCost: 0.0019,
              memoryCost: 0.0008,
              baseCost: 0.0003
            }
          }
        },
        optimizationRecommendations: [
          {
            title: 'Frame-extractor Resource Optimization',
            description: 'Consider reducing CPU allocation from 8 to 6 vCPU during off-peak hours to save ~30% on processing costs.'
          },
          {
            title: 'Solo-analyzer Batch Processing',
            description: 'Enable batch processing for small videos (<500 frames) to reduce per-frame overhead costs.'
          }
        ]
      };
    }
  },

  // Historical cost data endpoints
  getHistoricalCosts: async (startDate, endDate, granularity = 'daily') => {
    try {
      const response = await api.get(`/api/v1/monitoring/cost-history?startDate=${startDate}&endDate=${endDate}&granularity=${granularity}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch historical costs:', error);
      return {
        timeRange: { startDate, endDate, granularity },
        dataPoints: 0,
        data: [],
        error: error.message
      };
    }
  },

  getCostTrends: async (daysBack = 30, serviceName = null) => {
    try {
      const params = new URLSearchParams({ daysBack: daysBack.toString() });
      if (serviceName) {
        params.append('serviceName', serviceName);
      }
      
      const response = await api.get(`/api/v1/monitoring/cost-trends?${params}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch cost trends:', error);
      return {
        type: 'error',
        daysBack,
        service: serviceName,
        error: error.message
      };
    }
  },

  getCostForecast: async (daysAhead = 30, confidence = 0.8) => {
    try {
      const response = await api.get(`/api/v1/monitoring/cost-forecast?daysAhead=${daysAhead}&confidence=${confidence}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch cost forecast:', error);
      return {
        forecastPeriod: daysAhead,
        confidence,
        error: error.message,
        forecast: []
      };
    }
  }
};

export default monitoringApi;
